import {get} from 'lodash-es'
import React from 'react'
import PropTypes from 'prop-types'
import useSWR from 'swr'
import {useTranslation} from 'react-i18next'
import {Box} from '@chakra-ui/react'

import {useCustomerRewardsPromo} from '@paymentsOnDemand/public'

import useFeatureFlags from '../../../../hooks/useFeatureFlags'
import {
  COMPLETE,
  DECISION_SUPPORTED,
  DECISION_PENDING_ACKNOWLEDGED,
  useAcquisitionDecision,
} from '../../../../hooks/useAcquisitionDecision'
import {useRails} from '../../../../contexts/rails'
import {fetcher} from '../../../../../api'
import {NextScreenHeader} from '../../../../components/ScreenHeader'
import {NAV_CARD_INSET} from '../../../../components/SideNavV2/NavPanel'
import SupplierLayout from '../../../../layouts/SupplierLayout'

import ReimbursementRequired from './ReimbursementRequired'
import OverdueInvoicesDetails from '../../../../components/OverdueInvoicesDetails'
import SupplierRewardsCard from './SupplierRewardsCard'
import SupplierRewardsBanner from '../../../../components/SupplierRewardsBanner'
import {AuthProvider} from '../../../../contexts/authentication'
import CashinStepper from '../../../../components/CashinStepper'
import UnpaidInvoicesCard from './UnpaidInvoicesCard'
import PendingReasonsCard from './PendingReasonsCard'
import BankingDetailsPopup from '../../../../components/BankingDetailsPopup'

const Baseline = ({authenticityToken}) => {
  const {t} = useTranslation('supplierDashboard')
  const {
    isLoading: loadingFeatureData,
    data: {features},
  } = useFeatureFlags()
  const overdueInvoiceInfoEnabled = features && features.overdueInvoiceInfo
  const loadedFeatures = !loadingFeatureData && features
  const {organisation} = useRails()
  const {data: acquisitionDecision, isLoading: acquisitionDecisionLoading} =
    useAcquisitionDecision()

  const {showRewardPromoBanner, isLoading: isLoadingPromoStatus} =
    useCustomerRewardsPromo()

  const acknowledged =
    !acquisitionDecisionLoading &&
    acquisitionDecision.currentState === DECISION_PENDING_ACKNOWLEDGED

  const supported =
    !acquisitionDecisionLoading &&
    acquisitionDecision.currentState === DECISION_SUPPORTED

  const complete =
    !acquisitionDecisionLoading && acquisitionDecision.currentState === COMPLETE

  const historicalInvoicesFetchCompletedAt = get(
    organisation,
    'currentSupplier.historicalInvoicesFetchCompletedAt',
    false,
  )
  const daysPastMostOverdueInvoiceDueDate = get(
    organisation,
    'currentSupplier.daysPastMostOverdueInvoiceDueDate',
    0,
  )
  const showCashinStepper = get(
    organisation,
    'currentSupplier.showCashinStepper',
    false,
  )

  const {data, error} = useSWR('/api/supplier_dashboard', fetcher)

  const loading = !data && !error

  if (error && error.message === 'Unauthorized') {
    window.location.href = '/sign_in'
  }

  const amountOwedCents = get(
    data,
    'dashboard.reimbursement_required_at_cashin_amount.cents',
  )

  return (
    <AuthProvider authenticityToken={authenticityToken}>
      <SupplierLayout state="Dashboard">
        <Box
          as="main"
          flexGrow="1"
          backgroundColor="mld.neutral.200"
          pt={NAV_CARD_INSET}
          pb="sm"
          px="md"
          minHeight="100vh"
        >
          <Box mb="md">
            <NextScreenHeader
              isLoaded={!loading}
              text={t('welcome.title', {
                name: get(data, 'dashboard.supplier_display_name'),
              })}
            />
          </Box>

          {error &&
            error.message !== 'Unauthorized' &&
            error.name !== 'APINetworkError' && (
              <div>Error in retrieving dashboard information</div>
            )}
          {amountOwedCents > 0 &&
            !(
              daysPastMostOverdueInvoiceDueDate >= 25 &&
              loadedFeatures &&
              overdueInvoiceInfoEnabled
            ) && <ReimbursementRequired amountOwedCents={amountOwedCents} />}

          {acknowledged && <PendingReasonsCard />}

          <OverdueInvoicesDetails allowClose />

          {!isLoadingPromoStatus && showRewardPromoBanner && (
            <SupplierRewardsBanner />
          )}

          {showCashinStepper && (
            <Box mb="md">
              <CashinStepper />
            </Box>
          )}

          {historicalInvoicesFetchCompletedAt && (complete || supported) && (
            <UnpaidInvoicesCard />
          )}

          {(complete || supported) && <SupplierRewardsCard />}
          <BankingDetailsPopup />
        </Box>
      </SupplierLayout>
    </AuthProvider>
  )
}

Baseline.propTypes = {
  authenticityToken: PropTypes.string.isRequired,
}

export default Baseline
